import Vue from 'vue';
import _ from 'lodash';
import moment from 'moment';
import Api from "services/Api";
import store from 'store/index';
import {previousValid} from 'store/generators/step';
import {validDate} from 'components/form/validator/validations';
import {carePeriodTemplate, customHeadsOfLossTemplate} from 'store/modules/shared/templates';

/**
 * Mutation Types
 *
 * @type {Object}
 */
const mutationTypes = {
    SET: 'SET',
    UPDATE_FROM_SERVER: 'UPDATE_FROM_SERVER',
    ADD_CUSTOM_HEAD_OF_LOSS: 'ADD_CUSTOM_HEAD_OF_LOSS',
    DELETE_CUSTOM_HEAD_OF_LOSS: 'DELETE_CUSTOM_HEAD_OF_LOSS',
    UPDATE_CUSTOM_HEAD_OF_LOSS_VALUE: 'UPDATE_CUSTOM_HEAD_OF_LOSS_VALUE',
    ADD_CARE_PERIOD: 'ADD_CARE_PERIOD',
    UPDATE_CARE_PERIOD_VALUE: 'UPDATE_CARE_PERIOD_VALUE',
    UPDATE_CARE_VALUE: 'UPDATE_CARE_VALUE',
    UPDATE_INTEREST_VALUE: 'UPDATE_INTEREST_VALUE',
    DELETE_CARE_PERIOD: 'DELETE_CARE_PERIOD',
    RESET: 'RESET',
    RESET_INTEREST: 'RESET_INTEREST',
};

const numberVal = value => parseFloat(value || 0);

/**
 * Validation Rules.
 *
 * @type {Object}
 */
const rules = {};

/**
 * Initial State.
 *
 * @return {Object}
 */
function initialState() {
    return {
        generalDamagesInterest: 2.0,
        generalDamagesWithInterest: 0,

        pastLossOfEarnings: 0,
        pastLossOfEarningsInterest: 0.5,
        pastLossOfEarningsWithInterest: 0,

        pastReducedEarningsInterest: 0.5,
        pastReducedEarningsWithInterest: 0,

        // Past Cost of Services
        pastCostOfServices: 0,
        pastCostOfServicesInterest: 0.5,
        pastCostOfServicesWithInterest: 0,

        // Past Cost of Travel
        pastCostOfTravel: 0,
        pastCostOfTravelInterest: 0.5,
        pastCostOfTravelWithInterest: 0,

        // Misc Cost
        misc: 0,
        miscInterest: 0.5,
        miscWithInterest: 0,

        // Custom Heads of Loss
        headsOfLoss: [],
        customHeadsOfLossTotal: 0,
        customHeadsOfLossTotalWithInterest: 0,

        // Cost of Care
        careCalcType: null,
        pastCostOfCare: 0,
        pastCostOfCareInterest: 0.5,
        pastCostOfCareWithInterest: 0,
        periods: [carePeriodTemplate()],

        // Interest Date
        interestType: 'none',
        customInterestDate: null
    }
}

/**
 * Initial State
 *
 * @type {Object}
 */
const state = initialState();

/**
 * Return the payload required for the interest calculation modals
 *
 * @type {object}
 */
const modalPayload = (state) => {

    state.pastLossOfEarnings = store.getters['personal/claimant/getIncomeValue']('pastLossOfEarningsNetTotal');

    return {
        interimPayments: store.getters['personal/interimPayments/interimPayments'],
        assumedSettlement: store.getters['personal/claimant/getClaimInfo']('dateOfAssumedSettlement'),
        serviceOfProceedings: store.getters['personal/claimant/getClaimInfo']('dateOfServiceProceedings'),
        onsetOfSymptoms: store.getters['personal/claimant/getClaimInfo']('dateOfInjury'),
        assumedLifeExpectancy: store.getters['personal/claimant/getClaimantValue']('assumedLifeExpectancy'),
        deductibles: store.getters['personal/cruBenefits/all'],
        headsOfLoss: {
            ...state,
            generalDamages: store.getters['personal/generalDamages/getGeneralDamages'],
        },
        headOfLoss: {}
    }
};

/**
 * Getters.
 *
 * @type {Object}
 */
const getters = {
    value: state => key => state[key],
    getModalPayload: state => modalPayload(state),
    getCarePeriod: state => index => state.periods[index],
    getCustomHeadOfLoss: state => index => state.headsOfLoss[index],
    getAllCustomHeadOfLoss: state => state.headsOfLoss,
    customHeadsOfLossTotal: state => state.headsOfLoss.reduce((total, headOfLoss) => total + headOfLoss.value, 0),
    customHeadsOfLossTotalWithInterest: state => state.headsOfLoss.reduce((total, headOfLoss) => total + headOfLoss.interestAccrued, 0),
    allCarePeriodsValid: state => type => state.periods.reduce((valid, period) => {
        if (!valid) {
            return valid;
        }
        return moment(period.periodStart, 'DD/MM/YYYY').isValid() &&
            moment(period.periodEnd, 'DD/MM/YYYY').isValid() &&
            period.hoursPerDay > 0;
    }, true),
    interestDate: (state, getters, rootState) => {
        switch (state.interestType) {
            case "today":
                return moment().format("DD/MM/YYYY");
            case "assumed_settlement":
                return rootState.personal.claimant.claimInfo.dateOfAssumedSettlement;
            case "custom":
                return state.customInterestDate;
            case "none":
                return null;
        }
    },
    allCustomHeadOfLossValid: state => state.headsOfLoss.reduce((valid, headOfLoss) => {
        if (!valid) {
            return valid;
        }
        return headOfLoss.name !== null &&
            headOfLoss.name !== "" &&
            headOfLoss.value > 0
    }, true),
};

// Actions
const actions = {
    /**
     * Save the step by submitting to the server.
     *
     * @param getters
     * @param dispatch
     * @param state
     * @param payload
     */
    save({getters, dispatch, state}, payload) {
        // We might change this to something which
        // is a bit cleaner...
        payload.data = state;

        Api.claims.save(payload).then((response) => {
            return response;
        });
    },

    /**
     * Loads the step being received from the Server, to be placed in the state.
     *
     * @param dispatch
     * @param action
     * @param commit
     * @param step
     */
    load({dispatch, action, commit}, step) {
        const data = _.deeply(_.mapKeys)(step, (value, key) => _.camelCase(key)).data;
        commit(mutationTypes.UPDATE_FROM_SERVER, {data});
    },

    /**
     * Performs any processing once the data from the server has been stored.
     * This is a good place to do calculations or dispatch other actions which
     * are used to refresh/update calculated data on other steps/components.
     *
     * @param commit
     * @param dispatch
     */
    process({commit, dispatch}) {
        store.dispatch('personal/step/setStepNumberValid', {
            stepNo: 3,
            valid: true, // needs updating with real validity
        });
    },

    /**
     * Validate the step.
     *
     * @param commit
     */
    validate({commit, rootState}) {
        store.dispatch('personal/step/setStepNumberValid', {
            stepNo: 3,
            valid: previousValid(rootState, 3),
        });
    },

    /**
     * Resets the step back to defaults
     *
     * @param commit
     */
    reset({commit}) {
        store.dispatch('personal/step/setStepNumberValid', {
            stepNo: 3,
            valid: false,
        });

        commit(mutationTypes.RESET);
    },

    /**
     * Update Value
     *
     * @param commit
     * @param state
     * @param dispatch
     * @param key
     * @param value
     */
    set({commit, state, dispatch}, {key, value}) {
        if (state[key] === value) {
            return value;
        }
        commit(mutationTypes.SET, {key, value});
        //dispatch('updateSubTotal');
        return value;
    },

    /**
     * Add custom heads of loss
     *
     * @param commit
     */
    addCustomHeadOfLoss({commit}) {
        commit(mutationTypes.ADD_CUSTOM_HEAD_OF_LOSS);
    },

    /**
     * Update custom heads of loss
     *
     * @param commit
     * @param state
     * @param dispatch
     * @param key
     * @param value
     * @param index
     */
    setCustomHeadOfLossValue({commit, state, dispatch}, {key, value, index}) {
        if (state.headsOfLoss[index][key] === value) {
            return value;
        }
        commit(mutationTypes.UPDATE_CUSTOM_HEAD_OF_LOSS_VALUE, {key, value, index});
        return value;
    },

    /**
     * Delete custom heads of loss
     *
     * @param commit
     * @param index
     */
    deleteCustomHeadOfLoss({commit}, index) {
        commit(mutationTypes.DELETE_CUSTOM_HEAD_OF_LOSS, index);
    },

    /**
     * Add care period
     *
     * @param commit
     */
    addCarePeriod({commit}) {
        commit(mutationTypes.ADD_CARE_PERIOD);
    },

    /**
     * Delete care period
     *
     * @param commit
     * @param dispatch
     * @param state
     * @param index
     */
    deleteCarePeriod({commit, dispatch, state}, index) {
        commit(mutationTypes.DELETE_CARE_PERIOD, index);
        if (state.periods.length === 0) {
            dispatch('addCarePeriod');
        }
        return dispatch('updateCareTotal');
    },

    /**
     * Update care period value
     *
     * @param commit
     * @param state
     * @param key
     * @param value
     * @param index
     */
    updateCarePeriodValue({commit, state}, {key, value, index}) {
        // Prevent there being more than 24 hours in a day. Min/max attributes are buggy.
        if (key === 'hoursPerDay' && value > 24) {
            value = 24;
        }

        if (state.periods[index][key] === value) {
            return value;
        }
        commit(mutationTypes.UPDATE_CARE_PERIOD_VALUE, {key, value, index});
        return value;
    },

    /**
     * Calculate the care period total
     *
     * @param dispatch
     * @param state
     * @param index
     */
    calcCareTotalForPeriod: _.debounce(({dispatch, state}, {index}) => {
        if (validDate(state.periods[index].periodStart) && validDate(state.periods[index].periodEnd) && state.periods[index].hoursPerDay > 0) {
            return Api.calculators.monetary.detailedCare([state.periods[index]], state.periods[index].gratuitousCare)
                .then(care => dispatch('updateCarePeriod', {care, index}));
        }
    }, 500),

    /**
     * Update care period
     *
     * @param dispatch
     * @param commit
     * @param care
     * @param index
     */
    updateCarePeriod({dispatch, commit}, {care, index}) {
        Object.keys(care[0]).forEach((key) => {
            const value = care[0][key];
            commit(mutationTypes.UPDATE_CARE_PERIOD_VALUE, {key, value, index});
        });
        dispatch('updateCareTotal');
    },

    /**
     * Update care total value
     *
     * @param commit
     * @param state
     */
    updateCareTotal({commit, state}) {
        const value = state.periods.reduce((total, period) => total + numberVal(period.total), 0);
        commit(mutationTypes.SET, {key: 'pastCostOfCare', value});
    },

    /**
     * Calculate all the interest
     *
     * @param dispatch
     * @param state
     * @param rootGetters
     */
    calcAllInterest({dispatch, state, rootGetters}) {
        if (state.interestType === "none") {
            return dispatch('resetInterest');
        }
        if (state.interestType === 'custom' && (state.customInterestDate === '' || state.customInterestDate === null)) {
            return null;
        }

        const claimInfo = rootGetters['personal/claimant/getClaimInfo'];
        const claimant = rootGetters['personal/claimant/claimant'];

        if (moment(state.customInterestDate, 'DD/MM/YYYY').isSameOrBefore(moment(claimInfo('dateOfInjury'), 'DD/MM/YYYY'))) {
            return;
        }

        ///// This is giant hack so I don't have to re-write the API call for this, but this is bollocks
        const headsOfLoss = {
            ...state,
            pastLossOfEarnings: rootGetters['personal/claimant/getIncomeValue']('pastLossOfEarningsNetTotal'),
            generalDamages: rootGetters['personal/generalDamages/getGeneralDamages'],
        };

        return Api.calculators.monetary.interest(
            claimInfo('proceduralStatus'),
            claimInfo('dateOfAssumedSettlement'),
            claimInfo('dateOfServiceProceedings'),
            claimInfo('dateOfInjury'),
            claimant.lifeExpectancy.dateOfDeath,
            rootGetters['personal/cruBenefits/all'],
            headsOfLoss,
            rootGetters['personal/interimPayments/interimPayments'],
        ).then(data => dispatch('updateInterestValues', data));
    },

    updateInterestValues({commit}, data) {
        Object.keys(data).forEach((key) => {
            if (key.includes("custom_")) {
                commit(mutationTypes.UPDATE_CUSTOM_HEAD_OF_LOSS_VALUE, {
                    key: data[key]['key'],
                    value: data[key]['value'],
                    index: data[key]['index']
                });
            }
            commit(mutationTypes.SET, {key: key + 'WithInterest', value: data[key]});
        });
    },

    resetInterest({commit}) {
        commit(mutationTypes.RESET_INTEREST);
    }
};

// Mutations
const mutations = {
    /**
     * Set an object on the state.
     */
    [mutationTypes.SET](state, payload) {
        // Might be able to do this nicer with Object.assign
        Vue.set(state, payload.key, payload.value);
    },
    /**
     * Add data to the state from the server
     */
    [mutationTypes.UPDATE_FROM_SERVER](state, {data}) {
        state.generalDamagesInterest = data.generalDamagesInterest;
        state.generalDamagesWithInterest = data.generalDamagesWithInterest;
        state.pastLossOfEarnings = data.pastLossOfEarnings;
        state.pastLossOfEarningsInterest = data.pastLossOfEarningsInterest;
        state.pastLossOfEarningsWithInterest = data.pastLossOfEarningsWithInterest;
        state.pastReducedEarningsInterest = data.pastReducedEarningsInterest;
        state.pastReducedEarningsWithInterest = data.pastReducedEarningsWithInterest;
        state.pastCostOfServices = data.pastCostOfServices;
        state.pastCostOfServicesInterest = data.pastCostOfServicesInterest;
        state.pastCostOfServicesWithInterest = data.pastCostOfServicesWithInterest;
        state.pastCostOfTravel = data.pastCostOfTravel;
        state.pastCostOfTravelInterest = data.pastCostOfTravelInterest;
        state.pastCostOfTravelWithInterest = data.pastCostOfTravelWithInterest;
        state.misc = data.misc;
        state.miscInterest = data.miscInterest;
        state.miscWithInterest = data.miscWithInterest;
        state.headsOfLoss = data.headsOfLoss;
        state.customHeadsOfLossTotal = data.customHeadsOfLossTotal;
        state.customHeadsOfLossTotalWithInterest = data.customHeadsOfLossTotalWithInterest;
        state.careCalcType = data.careCalcType;
        state.pastCostOfCare = data.pastCostOfCare;
        state.pastCostOfCareInterest = data.pastCostOfCareInterest;
        state.pastCostOfCareWithInterest = data.pastCostOfCareWithInterest;
        state.periods = data.periods;
        state.interestType = data.interestType;
        state.customInterestDate = data.customInterestDate;
    },
    /**
     * Reset state.
     */
    [mutationTypes.RESET](state) {
        Object.assign(state, initialState());
    },
    /**
     * Add custom heads of loss data in the state.
     */
    [mutationTypes.ADD_CUSTOM_HEAD_OF_LOSS](state) {
        // if head of loss exists and has more than 1 value already, check the last values
        if (state.headsOfLoss && state.headsOfLoss.length > 0) {
            switch (true) {
                case state.headsOfLoss[state.headsOfLoss.length - 1].name === '':
                case state.headsOfLoss[state.headsOfLoss.length - 1].value <= 0:
                    return false;
            }
        }

        state.headsOfLoss.push(customHeadsOfLossTemplate());
    },
    /**
     * Delete custom heads of loss data in the state.
     */
    [mutationTypes.DELETE_CUSTOM_HEAD_OF_LOSS](state, index) {
        if (index === 0 && state.headsOfLoss.length === 1) {
            state.headsOfLoss = [];
        }
        state.headsOfLoss.splice(index, 1);
    },
    /**
     * Update custom heads of loss data in the state.
     */
    [mutationTypes.UPDATE_CUSTOM_HEAD_OF_LOSS_VALUE](state, {key, value, index}) {
        state.headsOfLoss[index][key] = value;
    },
    /**
     * Add care period data in the state.
     */
    [mutationTypes.ADD_CARE_PERIOD](state) {
        state.periods.push(carePeriodTemplate());
    },
    /**
     * Update care period in the state.
     */
    [mutationTypes.UPDATE_CARE_PERIOD_VALUE](state, {key, value, index}) {
        state.periods[index][key] = value;
    },
    /**
     * Delete care period in the state.
     */
    [mutationTypes.DELETE_CARE_PERIOD](state, data) {
        state.periods.splice(data.index, 1);
    },
    [mutationTypes.RESET_INTEREST](state) {
        Object.keys(state).forEach(key => {
            if (key.substr(-12) === "WithInterest") {
                state[key] = 0;
            }
        });
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}


// WEBPACK FOOTER //
// ./src/store/modules/personal/otherPastLosses.js